import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DialogProps {
    open: boolean
    onClose: () => void
    title: string
    description?: string
    children: ReactNode
    className?: string
}

export const Dialog: FC<DialogProps> = ({ open, onClose, title, description, children, className }): ReactNode => {
    const ref = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const dialog = ref.current
        if (!dialog) return

        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    return (
        <dialog
            ref={ref}
            aria-labelledby='dialog-title'
            onClose={onClose}
            onCancel={event => {
                event.preventDefault()
                onClose()
            }}
            onClick={event => {
                if (event.target === ref.current) onClose()
            }}
            className={cn(
                'm-auto max-h-[92vh] w-[calc(100%-1.5rem)] max-w-lg overflow-hidden rounded-3xl border border-border bg-card p-0 text-foreground shadow-2xl',
                'backdrop:bg-ink/60 backdrop:backdrop-blur-sm open:animate-in open:fade-in open:zoom-in-95',
                className
            )}
        >
            {open && (
                <div className='flex max-h-[92vh] flex-col'>
                    <div className='flex items-start justify-between gap-4 border-b border-border px-6 py-5'>
                        <div className='flex flex-col gap-1'>
                            <h2 id='dialog-title' className='text-xl font-bold'>
                                {title}
                            </h2>

                            {description && <p className='text-sm text-muted-foreground'>{description}</p>}
                        </div>

                        <button
                            type='button'
                            onClick={onClose}
                            className='-mr-2 -mt-1 rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            aria-label='Close'
                        >
                            <X className='size-5' />
                        </button>
                    </div>

                    <div className='overflow-y-auto px-6 py-5'>
                        {children}
                    </div>
                </div>
            )}
        </dialog>
    )
}
